import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, catchError, throwError, EMPTY } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (
        authService.SessionToken &&
        !AuthUtils.isTokenExpired(authService.SessionToken)
    ) {
        newReq = req.clone({
            headers: req.headers
                .set('Authorization', 'Bearer ' + authService.SessionToken)
                .set('refreshtoken', authService.refreshToken),
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // Exact-path match on the LAST URL segment so we don't
                // accidentally exclude future endpoints that merely contain
                // these strings (e.g. "/systemUserLoginHistory" or
                // "/userLogoutLog"). The interceptor's req.url is the full
                // URL at this point, so we extract the path and compare the
                // trailing segment.
                let lastSegment = '';
                try {
                    const pathname = new URL(req.url, 'http://_').pathname;
                    lastSegment = pathname.split('/').filter(Boolean).pop() || '';
                } catch {
                    // Malformed URL: fall through; both flags stay false.
                }
                const isSignInRequest = lastSegment === 'systemUserLogin';
                // The sales-partner app's embedded page (/embed/...) has no
                // admin session at all - it carries its own short-lived token.
                // Signing out and reloading there loses nothing and simply
                // runs the same failing request again, which reads as a page
                // that blinks forever. Let the page show its own error.
                const isEmbeddedPage =
                    typeof location !== 'undefined' &&
                    location.pathname.startsWith('/embed/');
                // signOut() posts to /userLogout and will 401 today (admin
                // JWT isn't verified by the user auth middleware). Without
                // this guard, the interceptor would call signOut() again
                // → infinite recursion.
                const isSignOutRequest =
                    lastSegment === 'userLogout' ||
                    req.url.includes('/admin-push/unsubscribe');

                if (!isSignInRequest && !isSignOutRequest && !isEmbeddedPage) {
                    // Sign out
                    authService.signOut();

                    // Reload the app
                    location.reload();

                    // Return EMPTY — page is reloading, no need to propagate
                    return EMPTY;
                }
            }

            return throwError(() => error);
        })
    );
};
